import React, { useRef, useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@/components/button";
import { IconButton } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useMediaQuery } from "@mui/material";
import { Close } from "@mui/icons-material";
import PreLoader from "@/components/preLoader";
import ProductCard from "./ProductCard";

import { selectLocalization } from "@/store/store/selectors";

import { useMutation } from "@apollo/client";
import { UPDATE_PRODUCTS } from "@/services/productService";

import { Colors } from "@/theme";
import useStyles from "./styles";
import { getStoreAddressString } from "@/utils/otherUtil";
import { STORE_INTEGRATION_TYPE } from "@/constants/config";
import { MERCHANT_ID } from "@/constants/env";

const AddSalesModal = ({
  open,
  onClose,
  store,
  products,
  onUpdateSuccess,
  stockTypes,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("sm"));
  const localization = useSelector(selectLocalization);

  const currentTime = useRef(dayjs(new Date()));

  const [productsForSales, setProductsForSales] = useState(
    products.map((product) => ({
      id: product.id,
      product_code: product.product_code,
      time: currentTime.current,
      stock_types: stockTypes.map((type) => ({ ...type, amount: "0" })),
      closing_stock: "0",
    }))
  );

  const isDisabled = !productsForSales?.find(
    (item) =>
      !!item.stock_types?.find(
        (stock_type) => parseFloat(stock_type.amount) > 0
      ) || parseFloat(item.closing_stock) > 0
  );

  const [updateProductsMutation, { loading }] = useMutation(UPDATE_PRODUCTS, {
    context: {
      headers: {
        merchantId: MERCHANT_ID,
        storeId: store.id,
      },
    },
    onCompleted: (data) => {
      onUpdateSuccess();
      onClose();
    },
  });

  const onChangeProduct = (product, key, value) => {
    setProductsForSales(
      productsForSales.map((item) =>
        item.id === product.id ? { ...item, [key]: value } : item
      )
    );
  };

  const onAdd = () => {
    updateProductsMutation({
      variables: {
        products: productsForSales
          .filter(
            (item) =>
              !!item.stock_types?.find(
                (stock_type) => parseFloat(stock_type.amount) > 0
              ) || parseFloat(item.closing_stock) > 0
          )
          ?.map((product) => ({
            id: product.id,
            stocks: product.stock_types
              ?.filter((stock_type) => parseFloat(stock_type.amount) > 0)
              ?.map((stock_type) => ({
                stock: parseFloat(stock_type.amount) * -1,
                stock_type_id: stock_type.id,
                update_time: product.time.toISOString().replace("Z", ""),
              })),
            ...(parseFloat(product.closing_stock) > 0 && {
              closing_stock: {
                stock: parseFloat(product.closing_stock),
                update_time: product.time.toISOString().replace("Z", ""),
              },
            }),
          })),
      },
    });
  };

  return (
    <>
      <Dialog
        className={classes.container}
        maxWidth={"lg"}
        fullWidth
        open={open}
        onClose={onClose}
      >
        <DialogTitle className={classes.title}>
          {t("dashboard_screen.manual_sales_data")}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
          }}
        >
          <Close fontSize="large" sx={{ color: Colors.GRAY_DARK }} />
        </IconButton>
        <DialogContent
          className={classes.content}
          sx={{ paddingX: isDesktop ? 25 : 2 }}
        >
          <Box display={"flex"} justifyContent={"center"} alignItems={"center"}>
            <img src={store.brand?.logo?.thumbnail} width={80} height={80} />
            <Box ml={4}>
              <Box textAlign={"center"} mb={3}>
                <span className={classes.storeNameText}>
                  {`${store.name} (${
                    store.integrations?.find(
                      (item) =>
                        item.integration_type ===
                        STORE_INTEGRATION_TYPE.LIVE_PRICE
                    )?.integration_id
                  })`}
                </span>
              </Box>
              <Box textAlign={"center"} maxWidth={220} margin={"auto"}>
                <span className={classes.addressText}>
                  {getStoreAddressString(store.address)}
                </span>
              </Box>
            </Box>
          </Box>

          <Grid container component={"main"} mt={10}>
            <Grid item xs={2} />
            <Grid item xs={2.5}>
              <Box className={classes.labelText}>
                {t("dashboard_screen.select_date")}
              </Box>
            </Grid>
            {stockTypes.map((stockType) => (
              <Grid item xs={2.5} key={stockType.id}>
                <Box className={classes.labelText}>
                  {t("dashboard_screen.volume_sold", { name: stockType.name })}
                </Box>
              </Grid>
            ))}
            <Grid item xs={2.5}>
              <Box className={classes.labelText}>
                {t("dashboard_screen.closing_volume")}
              </Box>
            </Grid>
          </Grid>
          {productsForSales?.map((product, id) => (
            <ProductCard
              key={id}
              isDesktop={isDesktop}
              product={product}
              localization={localization}
              onChange={(key, value) => onChangeProduct(product, key, value)}
            />
          ))}
          <Box paddingY={10} display={"flex"} justifyContent={"center"}>
            <Button
              disabled={isDisabled}
              color="primary"
              variant="contained"
              sx={{ mt: 4 }}
              className={classes.updateButton}
              onClick={onAdd}
            >
              {t("button_group.add")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {loading && <PreLoader size={40} />}
    </>
  );
};

export default AddSalesModal;
